/**
 * Person Detailseite - Natürliche oder Juristische Person
 * Mit Dokumenten-Ordner, Verknüpfungen, Kontaktdaten, strukturierten Rollen
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import {
  User, Building2, ArrowLeft, Edit, FileText, Upload, Loader2,
  Phone, Mail, MapPin, Trash2, Eye, Download, Link2, Briefcase,
  ExternalLink, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Rollen-Farben nach Kategorie
const ROLLEN_FARBEN = {
  betreuungsgericht: '#7c3aed',
  behoerde: '#3b82f6',
  bank: '#10b981',
  arzt_therapie: '#ec4899',
  pflege_heim: '#f59e0b',
  soziales: '#6366f1',
  familie: '#f97316',
  recht: '#0891b2',
  sonstiges: '#94a3b8',
};

function getRolleKategorie(rolle) {
  const MAP = {
    richter: 'betreuungsgericht', rechtspfleger: 'betreuungsgericht',
    sachbearbeiter: 'behoerde', 'sachbearbeiter/in': 'behoerde',
    ansprechpartner_bank: 'bank',
    hausarzt: 'arzt_therapie', facharzt: 'arzt_therapie', therapeut: 'arzt_therapie',
    heimleitung: 'pflege_heim', pflegeperson: 'pflege_heim',
    sozialarbeiter: 'soziales', 'sozialarbeiter/in': 'soziales', gesetzlicher_betreuer: 'soziales',
    angehoeriger: 'familie',
    anwalt: 'recht', notar: 'recht', steuerberater: 'recht',
    kontaktperson: 'sonstiges',
  };
  const slug = (rolle || '').toLowerCase().replace(/\s+/g, '_');
  return MAP[slug] || 'sonstiges';
}

export default function PersonDetailPage() {
  const params = useTabParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);
  const [dokumente, setDokumente] = useState([]);
  const [verknuepfteKlienten, setVerknuepfteKlienten] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Determine typ from URL - supports both /personenakten/:typ/:id and /personen/natuerlich/:id
  const typ = params.typ || (window.location.pathname.includes('/natuerlich/') ? 'natuerlich' : 'juristisch');
  const id = params.id;

  const loadData = async () => {
    setLoading(true);
    try {
      // Person laden
      let p;
      if (typ === 'natuerlich') {
        p = await betreuungAPI.getNatuerlichePerson(id);
      } else {
        p = await betreuungAPI.getJuristischePerson(id);
      }
      setPerson(p);

      // Dokumente für diese Person laden
      const docs = await betreuungAPI.listDokumente({ 
        person_id: id,
        person_typ: typ 
      });
      setDokumente(Array.isArray(docs) ? docs : []);

      // Verknüpfte Klienten
      if (p.verknuepfte_klienten) {
        setVerknuepfteKlienten(p.verknuepfte_klienten);
      }
    } catch (e) {
      toast.error('Fehler beim Laden');
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [id, typ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dokument hochladen
  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        await betreuungAPI.uploadDokument(file, {
          person_id: id,
          person_typ: typ,
          dokumententyp: 'personendokument'
        });
        toast.success(`${file.name} hochgeladen`);
      } catch { toast.error(`Fehler: ${file.name}`); }
    }
    setUploading(false);
    loadData();
  };

  // Dokument löschen
  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Dokument löschen?')) return;
    try {
      await betreuungAPI.deleteDokument(docId);
      toast.success('Gelöscht');
      loadData();
    } catch { toast.error('Fehler'); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Person nicht gefunden</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/personenakten')}>
          Zurück
        </Button>
      </div>
    );
  }

  const isNatuerlich = typ === 'natuerlich';
  const name = isNatuerlich 
    ? `${person.vorname || ''} ${person.nachname || ''}`.trim()
    : person.name || 'Unbenannt';

  return (
    <div className="space-y-6" data-testid="person-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personenakten')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isNatuerlich ? <User className="w-6 h-6 text-blue-500" /> : <Building2 className="w-6 h-6 text-purple-500" />}
            <h1 className="text-2xl font-bold text-slate-800">{name}</h1>
            <Badge
              variant="outline"
              style={isNatuerlich ? {
                borderColor: ROLLEN_FARBEN[getRolleKategorie(person.rolle)] || '#94a3b8',
                color: ROLLEN_FARBEN[getRolleKategorie(person.rolle)] || '#94a3b8',
              } : {}}
            >
              {isNatuerlich ? (person.rolle || 'Person') : (person.typ || 'Institution')}
            </Badge>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(isNatuerlich ? `/personenakten/natuerlich/${id}/bearbeiten` : `/personenakten/juristisch/${id}/bearbeiten`)}>
          <Edit className="w-4 h-4 mr-1" /> Bearbeiten
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="dokumente">Dokumente ({dokumente.length})</TabsTrigger>
          <TabsTrigger value="verknuepfungen">Verknüpfungen</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Kontaktdaten */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Kontaktdaten</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {person.telefon && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${person.telefon}`} className="text-blue-600 hover:underline">{person.telefon}</a>
                  </div>
                )}
                {person.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${person.email}`} className="text-blue-600 hover:underline">{person.email}</a>
                  </div>
                )}
                {(person.strasse || person.plz || person.ort) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      {person.strasse && <p>{person.strasse}</p>}
                      {(person.plz || person.ort) && <p>{person.plz} {person.ort}</p>}
                    </div>
                  </div>
                )}
                {!person.telefon && !person.email && !person.strasse && (
                  <p className="text-slate-400">Keine Kontaktdaten hinterlegt</p>
                )}
              </CardContent>
            </Card>

            {/* Zusätzliche Infos */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {isNatuerlich ? 'Persönliche Daten' : 'Institutionsdaten'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {isNatuerlich ? (
                  <>
                    {person.geburtsdatum && <p><strong>Geburtsdatum:</strong> {new Date(person.geburtsdatum).toLocaleDateString('de-DE')}</p>}
                    {person.beziehung && <p><strong>Beziehung:</strong> {person.beziehung}</p>}
                    {person.position && <p><strong>Position:</strong> {person.position}</p>}
                  </>
                ) : (
                  <>
                    {person.ansprechpartner && <p><strong>Ansprechpartner:</strong> {person.ansprechpartner}</p>}
                    {person.aktenzeichen && <p><strong>Aktenzeichen:</strong> {person.aktenzeichen}</p>}
                    {person.kundennummer && <p><strong>Kundennummer:</strong> {person.kundennummer}</p>}
                    {person.website && <p><strong>Website:</strong> <a href={person.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{person.website}</a></p>}
                  </>
                )}
                {person.notizen && (
                  <div className="pt-2 border-t">
                    <p className="text-slate-500">{person.notizen}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Dokumente Tab */}
        <TabsContent value="dokumente" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-500 text-sm">Alle Dokumente zu {name}</p>
            <div>
              <input ref={fileInputRef} type="file" multiple className="hidden"
                onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Hochladen
              </Button>
            </div>
          </div>

          {dokumente.length > 0 ? (
            <div className="grid gap-2">
              {dokumente.map(dok => (
                <div key={dok.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:bg-slate-50 group">
                  <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{dok.dateiname || dok.titel || 'Dokument'}</p>
                    <p className="text-xs text-slate-500">
                      {dok.created_date && new Date(dok.created_date).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {dok.datei_url && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => window.open(`${BACKEND_URL}${dok.datei_url}`, '_blank')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" asChild>
                          <a href={`${BACKEND_URL}${dok.datei_url}`} download>
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteDoc(dok.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">Keine Dokumente vorhanden</p>
                <Button variant="outline" className="mt-4" onClick={() => fileInputRef.current?.click()}>
                  Erstes Dokument hochladen
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Verknüpfungen Tab */}
        <TabsContent value="verknuepfungen" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Verknüpfte Klienten
              </CardTitle>
            </CardHeader>
            <CardContent>
              {verknuepfteKlienten.length > 0 ? (
                <div className="space-y-2">
                  {verknuepfteKlienten.map((k, i) => {
                    const rolleKat = getRolleKategorie(person.rolle);
                    const farbe = ROLLEN_FARBEN[rolleKat] || '#94a3b8';
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:shadow-sm cursor-pointer transition-all"
                        onClick={() => navigate(`/akten/${k.id}`)}
                        data-testid={`verknuepfter-klient-${i}`}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${farbe}15` }}>
                          <Briefcase className="w-4 h-4" style={{ color: farbe }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800">
                            {k.vorname} {k.nachname}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            {k.beziehung_art && (
                              <Badge variant="secondary" className="text-[10px]">{k.beziehung_art}</Badge>
                            )}
                            {k.beziehung_details && (
                              <span className="text-xs text-slate-500">{k.beziehung_details}</span>
                            )}
                            {k.fallnummer && (
                              <span className="text-xs text-slate-400">Az: {k.fallnummer}</span>
                            )}
                            {(k.beginn_datum || k.ende_datum) && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {k.beginn_datum && new Date(k.beginn_datum).toLocaleDateString('de-DE')}
                                {k.beginn_datum && k.ende_datum && ' - '}
                                {k.ende_datum && new Date(k.ende_datum).toLocaleDateString('de-DE')}
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-300 shrink-0" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">Keine verknüpften Klienten</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
